/**
 * Base component to generate tree view in a vertical row. It basically
 * controls nodes and its state (collapsed and expanded), loading nodes in an
 * asynchronous way (using promises) and gives the parent
 * the role of rendering the row.
 * It is nested in the Governance.js component.
 */

import store from '../../stores/store';
import React from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
// load css styles
import './tree-view.css';
// import * as local from '../../helpers/localStorage';
const TreeView = observer(
    class TreeView extends React.Component {
        constructor(props) {
            super(props);
            this.nodeClick = this.nodeClick.bind(this);
            this.onClick = this.onClick.bind(this);
        }

        componentWillMount() {
            //console.log('componentWillMount');

            this.loadNodes().then(res => this.setState({ root: res }));
        }

        /**
         * it runs before render
         * @param  {props} component props
         */
        componentWillReceiveProps(props) {
            //console.log('TreeView componentWillReceiveProps', props);
            // Removed this code as it's killing my state for download in governance
            // console.log(store.groups.isDownloading);
            // If we are downloading then do ot reload tree
            if (store.groups.isDownloading === 0) {
                this.setState({
                    root: null,
                });

                this.loadNodes().then(res => this.setState({ root: res }));
            }
        }

        /**
         * Load the objects that will be linked to the nodes of the tree
         * @param  {object} parent The parent node to load items into
         */
        loadNodes(parent) {
            const func = this.props.onGetNodes;

            if (!func) {
                return null;
            }

            const pitem = parent ? parent.item : undefined;
            let res = func(pitem);

            // is not a promise ?
            if (!res || !res.then) {
                // force node resolution by promises
                res = Promise.resolve(res);
            }

            // create nodes wrapper when nodes are resolved
            const self = this;
            return res.then(items => {
                if (!items) {
                    return [];
                }

                const nodes = self.createNodes(items);
                return nodes;
            });
        }

        /**
         * Create nodes from the list of items to add in the tree
         * @param  {Array} items [categories]
         * @return {object}  nodes [nodes ]
         */
        createNodes(items) {
            const funcInfo = this.props.checkLeaf;
            return items.map(item => {
                const leaf = funcInfo ? funcInfo(item) : false;
                return {
                    item,
                    state: 'collapsed',
                    children: null,
                    leaf,
                };
            });
        }

        /**
         * Create the React components of the nodes to be displayed in the tree
         * @return {Component} ReactCSSTransitionGroup [inside div's]
         */
        createNodesView() {
            const self = this;
            // get the item being expanded (for animation)
            const expitem = this.state.expitem;

            // recursive function to create the expanded tree in a list
            const mountList = function(nlist, level, parentkey) {
                let count = 0;
                const lst = [];

                // is the root being rendered
                if (!parentkey && self.props.title) {
                    lst.push(self.props.title);
                }

                nlist.forEach(node => {
                    const key = (parentkey ? `${parentkey}.` : '') + count;
                    const row = self.createNodeRow(node, level, key);

                    lst.push(row);
                    if (node.state !== 'collapsed' && !node.leaf && node.children) {
                        lst.push(mountList(node.children, level + 1, key, expitem === node));
                    }
                    count++;
                });

                // the children div key
                const divkey = `${parentkey || ''}ch`;

                // children are inside a div, in order to animate collapsing/expanding
                return (
                    <ReactCSSTransitionGroup
                        key={`${divkey}trans`}
                        transitionName="node"
                        transitionLeaveTimeout={250}
                        transitionEnterTimeout={250}
                    >
                        {lst}
                    </ReactCSSTransitionGroup>
                );
            };

            return mountList(this.state.root, 0, false);
        }

        /**
         * Resolve icon depending if node is collapsed or expanded
         * @param  {object} node  [node = category]
         */
        resolveIcon(node) {
            const p = this.props;

            let icon;
            if (node.leaf) {
                icon = p.iconLeaf;
            } else {
                icon = node.state !== 'collapsed' ? p.iconMinus : p.iconPlus;
            }

            if (typeof icon === 'function') {
                icon = icon(node.item);
            }

            if (typeof icon === 'string') {
                if (!node.leaf) {
                    const className = `fa fa-${icon} fa-fw`;
                    icon = <i className={className} />;
                }
            }

            return icon;
        }

        /**
         * Create node row containing the content of the node
         * @param  {object} node  [governance category]
         * @param  {number} level [level starts at 0 to 2]
         * @param  {numbeer} key   [key - position of the node example 0, 0.0, 0.0.0, 1, 1.0, 1.0.0 ]
         * @return {function}       [outRender function]
         */
        createNodeRow(node, level, key) {
            const p = this.props;
            // get the node content
            const content = p.innerRender ? p.innerRender(node.item) : node.item;

            const icon = this.resolveIcon(node);

            const waitIcon =
                node.state === 'expanding' ? <div className="fa fa-refresh fa-fw fa-spin" /> : null;

            // the content
            const nodeIcon = node.leaf ? (
                icon
            ) : (
                <a className="" onClick={this.nodeClick} data-item={key}>
                    {icon}
                </a>
            );

            const nodeRow = (
                <div
                    key={node.item.GovCatId}
                    className="node"
                    style={{ marginLeft: `${level * p.indent}px` }}
                >
                    {node.item.children && (
                        <span className="node-link expand-collapse-block">
                            {nodeIcon}
                            {waitIcon}
                        </span>
                    )}
                    {!node.item.children &&
                        level !== 0 && (
                            <span className="node-link expand-collapse-block">
                                {nodeIcon}
                                {waitIcon}
                            </span>
                        )}
                    <span
                        className="node-link folder-title-block"
                        onClick={this.onClick.bind(this, node, level, key)}
                    >
                        <span className="folder-title-padding">{content}</span>
                        {node.item.HasUnreadDocuments && (
                            <span className="ml-1 badge badge-pill badge-notify">New</span>
                        )}
                    </span>
                </div>
            );

            return p.outerRender ? p.outerRender(nodeRow, node.item) : nodeRow;
        }

        /**
         * On the click of a category display related document
         * @param  {object} node  [governance category]
         * @param  {number} level [level starts at 0 to 2]
         * @param  {number} key   [key - position of the node example 0, 0.0, 0.0.0, 1, 1.0, 1.0.0 ]
         */
        onClick(node, level, key) {
            if (this.props.onContentClick) {
                this.props.onContentClick(node, level, key);
            }
        }

        /**
         * On the node arrow click expands/collapses node
         * @param  {object} evt  [event with key/value collection]
         */
        nodeClick(evt) {
            const key = evt.currentTarget.getAttribute('data-item');

            let lst = this.state.root;
            let node = null;

            key.split('.').forEach(index => {
                node = lst[Number(index)];
                lst = node.children;
            });

            if (node.state === 'collapsed') {
                this.expandNode(node);
            } else {
                this.collapseNode(node);
            }
        }

        /**
         * Expand a given node
         * @param  {object} node [node = category arrow]
         */
        expandNode(node) {
            // children are not loaded ?
            if (!node.children) {
                node.state = 'expanding';
                this.forceUpdate();

                // load the children
                const self = this;
                this.loadNodes(node).then(res => {
                    node.state = 'expanded';
                    node.children = res;
                    // force tree to show the new expanded node
                    self.forceUpdate();
                });
            } else {
                node.state = 'expanded';
                // force tree to show the new expanded node
                this.forceUpdate();
            }
        }

        /**
         * Collapse a given node and refresh the tree
         * @param  {object} node [node = category arrow]
         */
        collapseNode(node) {
            node.state = 'collapsed';
            this.forceUpdate();
        }

        /**
         * Render the tree
         * @return {html element} div [html element]
         */
        render() {
            // console.log('TreeView render');

            const root = this.state ? this.state.root : null;
            if (!root) {
                // const self = this;

                return <i className="fa fa-refresh fa-fw fa-spin" />;
            }

            return <div className="tree-view">{this.createNodesView(root)}</div>;
        }
    },
);

TreeView.propTypes = {
    // array containing the items to be displayed
    root: PropTypes.array,
    // called to load the nodes in the format function(item): promise
    onGetNodes: PropTypes.func,
    // called to render the content of the node, beside the button
    // it will return the string or component to be displayed
    innerRender: PropTypes.func,
    // (optional) called to wrap the whole node inside another component
    outerRender: PropTypes.func,
    // optional title to be displayed on the top of the treeview
    title: PropTypes.any,
    // opitional. Check if node has children or is a leaf node
    checkLeaf: PropTypes.func,
    // name of fontawesome icon or function to return a name
    iconPlus: PropTypes.any,
    iconMinus: PropTypes.any,
    iconLeaf: PropTypes.any,
    iconSize: PropTypes.number,
    // the indentation of each node level, in pixels
    indent: PropTypes.number,
    onContentClick: PropTypes.func,
};

TreeView.defaultProps = {
    iconPlus: 'angle-right', // 'plus-square-o',
    iconMinus: 'angle-down', // 'minus-square-o',
    iconLeaf: '', // 'circle-thin'
    iconSize: 1,
    indent: 16,
};

export default TreeView;



// WEBPACK FOOTER //
// ./src/containers/_shared/TreeView.js